import { ulid } from "ulid";

import type { ExcelAgentSource } from "../agentTimeline";
import type { CitationNoteIndex, CitationNoteRecord } from "../citationNotes";

/**
 * Research evidence collected while one Excel operation runs.
 *
 * A paused run resumes into a fresh executor with empty accumulators, so
 * without this the model would re-run research it already paid for and, worse,
 * cited writes would fail because the citation index that resolves their IDs
 * was rebuilt empty. The pack is the operation's own memory of what it learned.
 *
 * Held only in the in-memory operation registry. The checkpoint that reaches
 * `sessionStorage` carries a version and a policy fingerprint, never answers,
 * citations, or guidance.
 */

export const EXCEL_OPERATION_EVIDENCE_VERSION = 1 as const;

/**
 * Per-answer storage cap. Research answers are usually a few thousand
 * characters; the cap bounds the pathological one rather than the normal case.
 */
export const EXCEL_EVIDENCE_ANSWER_MAX_CHARS = 8_000;
/**
 * Safety ceiling on distinct research calls, not a working-set limit.
 *
 * The pack holds one entry per question and is the operation's complete record
 * — what a resumed run reuses instead of paying for the same research twice.
 * Trimming it to fit the model's context is the renderer's job
 * (`EXCEL_EVIDENCE_RENDER_BUDGET_CHARS`), and doing it here instead would
 * delete findings the operation still owns while telling no one. The ceiling
 * sits far above what an executor run can produce, so reaching it is an
 * anomaly; when it is reached the pack is marked truncated, which the renderer
 * states to the model rather than presenting a partial record as the whole one.
 */
export const EXCEL_EVIDENCE_MAX_CALLS = 200;
/** Default budget for what a resumed run replays back to the model. */
export const EXCEL_EVIDENCE_RENDER_BUDGET_CHARS = 24_000;

const ANSWER_TRUNCATION_MARKER = (omitted: number): string =>
  `\n[Evidence truncated: ${omitted} characters omitted.]`;
const RENDER_TRUNCATION_MARKER =
  "[Earlier research in this operation was omitted to fit the context budget. " +
  "Treat the omitted work as done, not as missing.]";

export interface ExcelEvidenceCall {
  readonly evidenceId: string;
  readonly tool: string;
  /** The question as asked, used for exact-match reuse. */
  readonly question: string;
  /** The answer as stored, already truncated if it exceeded the cap. */
  readonly answer: string;
  /** Characters actually stored, so a truncated answer never reports its original size. */
  readonly answerChars: number;
  readonly answerTruncated: boolean;
  readonly citationIds: readonly string[];
  readonly stepId?: string;
  readonly capturedAt: string;
}

export interface ExcelOperationEvidence {
  readonly version: typeof EXCEL_OPERATION_EVIDENCE_VERSION;
  readonly operationId: string;
  /** Digest of the policy every call in this pack was collected under. */
  readonly policyFingerprint: string;
  readonly capturedAt: string;
  readonly calls: readonly ExcelEvidenceCall[];
  /** Raw provider citation envelopes, defensively copied and frozen. */
  readonly citations: readonly unknown[];
  readonly sources: readonly ExcelAgentSource[];
  readonly citationNoteIndex: Readonly<CitationNoteIndex>;
  readonly citedValuesByUuid: Readonly<Record<string, string>>;
  readonly totalAnswerChars: number;
  /** True once any answer was shortened or any call was dropped. */
  readonly truncated: boolean;
}

export interface ExcelOperationPolicyInput {
  /** Resolved Skill ids in resolver order, which is guidance precedence. */
  readonly skillIds?: readonly string[];
  /** Exact resolved guidance bytes, or absent for an unguided operation. */
  readonly guidance?: string;
  readonly dataSources?: readonly string[];
  readonly userFileIds?: readonly string[];
  readonly selectionMode?: string;
}

export interface AppendExcelOperationEvidenceInput {
  readonly current?: ExcelOperationEvidence;
  readonly operationId: string;
  readonly policyFingerprint: string;
  readonly tool: string;
  readonly question: string;
  readonly answer: string;
  readonly stepId?: string;
  readonly citations?: readonly unknown[];
  readonly sources?: readonly ExcelAgentSource[];
  readonly citationNoteIndex?: Readonly<CitationNoteIndex>;
  readonly citedValuesByUuid?: Readonly<Record<string, string>>;
  readonly evidenceId?: string;
  readonly capturedAt?: string;
}

/**
 * 64-bit FNV-1a over a length-delimited encoding, rendered as hex.
 *
 * This detects an accidental policy mismatch — a pack reaching a run that
 * resolved different Skills, different guidance, or a different research scope.
 * It is not a tamper-proof MAC, and nothing security-relevant depends on it;
 * the registry it guards is in-memory and per-account already.
 */
function digest(parts: readonly string[]): string {
  // Length-delimited so ["ab","c"] and ["a","bc"] cannot encode identically.
  const encoded = parts.map((part) => `${part.length}:${part}`).join("|");
  let low = 0x811c9dc5;
  let high = 0x01000193;
  for (let index = 0; index < encoded.length; index += 1) {
    const code = encoded.charCodeAt(index);
    low = Math.imul(low ^ code, 0x01000193) >>> 0;
    high = Math.imul(high ^ (code + index), 0x85ebca6b) >>> 0;
  }
  return `${low.toString(16).padStart(8, "0")}${high.toString(16).padStart(8, "0")}`;
}

/**
 * Fingerprints the policy an operation's research runs under.
 *
 * Guidance is hashed in full rather than by length, because two different
 * policies of the same length are exactly the case a length would miss.
 */
export function excelOperationPolicyFingerprint(
  input: ExcelOperationPolicyInput
): string {
  // Each list contributes its own count and its own entries, so no arrangement
  // of separators inside a value can imitate a different list.
  const list = (values: readonly string[] | undefined): string[] => [
    String(values?.length ?? 0),
    ...(values ?? []),
  ];
  return digest([
    "v1",
    ...list(input.skillIds),
    input.guidance ?? "",
    ...list(input.dataSources),
    ...list(input.userFileIds),
    input.selectionMode ?? "",
  ]);
}

/** Whitespace and case are not part of what makes two questions the same. */
export function normalizeEvidenceQuestion(question: string): string {
  return question.trim().replace(/\s+/g, " ").toLowerCase();
}

function cloneAndFreeze<T>(value: T): T {
  const clone = JSON.parse(JSON.stringify(value)) as T;
  const freeze = (entry: unknown): void => {
    if (!entry || typeof entry !== "object" || Object.isFrozen(entry)) return;
    for (const child of Object.values(entry as Record<string, unknown>)) {
      freeze(child);
    }
    Object.freeze(entry);
  };
  freeze(clone);
  return clone;
}

/**
 * Copy citation payloads instead of retaining the caller's objects.
 *
 * The provider envelopes handed to the pack are also handed to the timeline and
 * to chat persistence, both of which may reshape them. Evidence that mutated
 * underneath a paused operation would restore something other than what was
 * captured.
 */
function retainCitations(
  current: readonly unknown[],
  incoming: readonly unknown[] | undefined
): readonly unknown[] {
  if (!incoming?.length) return current;
  const retained = [...current];
  for (const citation of incoming) {
    if (!citation || typeof citation !== "object") continue;
    try {
      retained.push(cloneAndFreeze(citation));
    } catch {
      // A citation that cannot be safely copied is dropped rather than kept by
      // reference; its ID still resolves through the note index.
    }
  }
  return Object.freeze(retained);
}

function retainSources(
  current: readonly ExcelAgentSource[],
  incoming: readonly ExcelAgentSource[] | undefined
): readonly ExcelAgentSource[] {
  if (!incoming?.length) return current;
  const byId = new Map(current.map((source) => [source.id, source]));
  for (const source of incoming) {
    if (!source?.id || byId.has(source.id)) continue;
    byId.set(source.id, cloneAndFreeze(source));
  }
  return Object.freeze([...byId.values()]);
}

/**
 * Field-wise enrichment, matching the run-time citation index: a later sparse
 * record adds what it knows and never removes what an earlier one established.
 */
function retainCitationNotes(
  current: Readonly<CitationNoteIndex>,
  incoming: Readonly<CitationNoteIndex> | undefined
): Readonly<CitationNoteIndex> {
  if (!incoming || Object.keys(incoming).length === 0) return current;
  const merged: CitationNoteIndex = {};
  for (const [citationId, record] of Object.entries(current)) {
    merged[citationId] = { ...record };
  }
  for (const [citationId, record] of Object.entries(incoming)) {
    const existing: CitationNoteRecord = merged[citationId] ?? {
      citation_id: citationId,
    };
    for (const [key, value] of Object.entries(record)) {
      if (value) (existing as unknown as Record<string, string>)[key] = value;
    }
    existing.citation_id = citationId;
    merged[citationId] = existing;
  }
  return cloneAndFreeze(merged);
}

function storedAnswer(answer: string): {
  answer: string;
  truncated: boolean;
} {
  if (answer.length <= EXCEL_EVIDENCE_ANSWER_MAX_CHARS) {
    return { answer, truncated: false };
  }
  const kept = answer.slice(0, EXCEL_EVIDENCE_ANSWER_MAX_CHARS);
  return {
    answer: `${kept}${ANSWER_TRUNCATION_MARKER(answer.length - kept.length)}`,
    truncated: true,
  };
}

const CITATION_ID_TAG = /<u\b[^>]*\bid\s*=\s*(?:"([^"]+)"|'([^']+)')/gi;

function citationIdsInAnswer(answer: string): readonly string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  CITATION_ID_TAG.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = CITATION_ID_TAG.exec(answer)) !== null) {
    const id = (match[1] ?? match[2] ?? "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return Object.freeze(ids);
}

function finalize(input: {
  operationId: string;
  policyFingerprint: string;
  capturedAt: string;
  calls: readonly ExcelEvidenceCall[];
  citations: readonly unknown[];
  sources: readonly ExcelAgentSource[];
  citationNoteIndex: Readonly<CitationNoteIndex>;
  citedValuesByUuid: Readonly<Record<string, string>>;
  droppedCalls: boolean;
}): ExcelOperationEvidence {
  const calls = Object.freeze(input.calls.map((call) => Object.freeze(call)));
  return Object.freeze({
    version: EXCEL_OPERATION_EVIDENCE_VERSION,
    operationId: input.operationId,
    policyFingerprint: input.policyFingerprint,
    capturedAt: input.capturedAt,
    calls,
    citations: Object.freeze([...input.citations]),
    sources: Object.freeze([...input.sources]),
    citationNoteIndex: input.citationNoteIndex,
    citedValuesByUuid: Object.freeze({ ...input.citedValuesByUuid }),
    totalAnswerChars: calls.reduce((total, call) => total + call.answerChars, 0),
    truncated:
      input.droppedCalls || calls.some((call) => call.answerTruncated),
  });
}

/** An empty pack, used to seed an operation that has restored citations only. */
export function createExcelOperationEvidence(input: {
  readonly operationId: string;
  readonly policyFingerprint: string;
  readonly citations?: readonly unknown[];
  readonly citationNoteIndex?: Readonly<CitationNoteIndex>;
  readonly citedValuesByUuid?: Readonly<Record<string, string>>;
  readonly sources?: readonly ExcelAgentSource[];
  readonly capturedAt?: string;
}): ExcelOperationEvidence {
  return finalize({
    operationId: input.operationId,
    policyFingerprint: input.policyFingerprint,
    capturedAt: input.capturedAt ?? new Date().toISOString(),
    calls: [],
    citations: retainCitations([], input.citations),
    sources: retainSources([], input.sources),
    citationNoteIndex: retainCitationNotes({}, input.citationNoteIndex),
    citedValuesByUuid: input.citedValuesByUuid ?? {},
    droppedCalls: false,
  });
}

/**
 * Adds one completed research call to an operation's evidence.
 *
 * Rejects a pack from another operation or another policy outright. Silently
 * accepting either would let a resumed run present findings gathered under
 * guidance or a source allowlist that no longer applies.
 */
export function appendExcelOperationEvidence(
  input: AppendExcelOperationEvidenceInput
): ExcelOperationEvidence {
  if (input.current && input.current.operationId !== input.operationId) {
    throw new Error("Research evidence cannot cross Excel operations.");
  }
  if (
    input.current &&
    input.current.policyFingerprint !== input.policyFingerprint
  ) {
    throw new Error("Research evidence cannot cross Skill policies.");
  }

  const capturedAt = input.capturedAt ?? new Date().toISOString();
  const stored = storedAnswer(input.answer);
  const call: ExcelEvidenceCall = {
    evidenceId: input.evidenceId ?? `xlresearch_${ulid()}`,
    tool: input.tool,
    question: input.question.trim(),
    answer: stored.answer,
    answerChars: stored.answer.length,
    answerTruncated: stored.truncated,
    citationIds: citationIdsInAnswer(stored.answer),
    ...(input.stepId ? { stepId: input.stepId } : {}),
    capturedAt,
  };

  // One entry per question. A resumed run replays this record and can restate
  // a question it already covers, and a truncated stored answer is deliberately
  // not offered for reuse — so the same question legitimately arrives twice.
  // Appending each arrival would grow the pack on every resume until the oldest
  // genuine findings were pushed out of it.
  const previousCalls = input.current?.calls ?? [];
  const normalizedQuestion = normalizeEvidenceQuestion(call.question);
  const duplicateIndex = previousCalls.findIndex(
    (existing) =>
      existing.tool === call.tool &&
      normalizeEvidenceQuestion(existing.question) === normalizedQuestion
  );
  const allCalls =
    duplicateIndex === -1
      ? [...previousCalls, call]
      : previousCalls.map((existing, index) =>
          // The newer answer supersedes in place. Position is kept so the
          // record still reads in the order the operation did the work.
          index === duplicateIndex && existing.answer !== call.answer ? call : existing
        );
  const calls = allCalls.slice(-EXCEL_EVIDENCE_MAX_CALLS);
  return finalize({
    operationId: input.operationId,
    policyFingerprint: input.policyFingerprint,
    capturedAt,
    calls,
    citations: retainCitations(
      input.current?.citations ?? [],
      input.citations
    ),
    sources: retainSources(input.current?.sources ?? [], input.sources),
    citationNoteIndex: retainCitationNotes(
      input.current?.citationNoteIndex ?? {},
      input.citationNoteIndex
    ),
    citedValuesByUuid: {
      ...(input.current?.citedValuesByUuid ?? {}),
      ...(input.citedValuesByUuid ?? {}),
    },
    droppedCalls:
      Boolean(input.current?.truncated) || calls.length < allCalls.length,
  });
}

/**
 * A prior answer to exactly this question, or nothing.
 *
 * A truncated answer is never offered for reuse: replaying a shortened result
 * as if it were the whole finding is worse than asking again.
 */
export function findReusableExcelEvidence(
  pack: ExcelOperationEvidence | undefined,
  query: { readonly tool: string; readonly question: string }
): ExcelEvidenceCall | undefined {
  if (!pack) return undefined;
  const normalized = normalizeEvidenceQuestion(query.question);
  if (!normalized) return undefined;
  return pack.calls.find(
    (call) =>
      call.tool === query.tool &&
      !call.answerTruncated &&
      normalizeEvidenceQuestion(call.question) === normalized
  );
}

/**
 * Renders locked evidence as one user turn for a resumed run.
 *
 * Budgeted from the newest call backwards, because a resumed run is continuing
 * the work it was doing most recently. Dropped calls are announced rather than
 * silently omitted, so the model does not read a partial pack as the whole
 * record and decide to redo the rest.
 */
export function renderExcelOperationEvidence(
  pack: ExcelOperationEvidence | undefined,
  options: { readonly budgetChars?: number } = {}
): string | undefined {
  if (!pack || pack.calls.length === 0) return undefined;
  const budget = options.budgetChars ?? EXCEL_EVIDENCE_RENDER_BUDGET_CHARS;
  const rendered: string[] = [];
  let used = 0;
  let omitted = pack.truncated;
  for (let index = pack.calls.length - 1; index >= 0; index -= 1) {
    const call = pack.calls[index];
    const block = [
      `<research_call index="${index + 1}" tool="${call.tool}">`,
      "<question>",
      call.question,
      "</question>",
      "<answer>",
      call.answer,
      "</answer>",
      "</research_call>",
    ].join("\n");
    if (used + block.length > budget && rendered.length > 0) {
      omitted = true;
      continue;
    }
    used += block.length;
    rendered.unshift(block);
  }
  return [
    "COMPLETED RESEARCH FOR THIS REQUEST (locked)",
    "These calls already ran under the policy in force for this request. Treat their answers as the factual record.",
    "Preserve the citation IDs inside the answers; cited writes resolve their Notes from them.",
    "Do not repeat a question answered here. Research only a question this record does not cover.",
    ...(omitted ? ["", RENDER_TRUNCATION_MARKER] : []),
    "",
    "<completed_research>",
    ...rendered,
    "</completed_research>",
  ].join("\n");
}
